import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ProfilePicturesAPI } from '../api/ProfilePicturesAPI';
import ConfirmDeleteModal from './dashboard/components/ConfirmDeleteModal';

import './dashboard/components/Marker-UI.css';
import './ProfilePictures.css';
import PageTitle from '../components/PageTitle';

const PAGE_SIZE = 48;

const formatDate = (iso) => {
  if (!iso) return '';
  const date = new Date(iso);
  return Number.isNaN(date.valueOf())
    ? ''
    : date.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

function PictureCard({ item, busy, onRemovePicture, onDeleteUser }) {
  const [armed, setArmed] = useState(false);

  // "Bild entfernen" needs a second click within a few seconds instead of a modal,
  // so that reviewing many pictures stays fast.
  useEffect(() => {
    if (!armed) return undefined;
    const timer = setTimeout(() => setArmed(false), 3500);
    return () => clearTimeout(timer);
  }, [armed]);

  const handleRemoveClick = () => {
    if (!armed) { setArmed(true); return; }
    setArmed(false);
    onRemovePicture(item);
  };

  return (
    <div className={'pp-card' + (busy ? ' pp-card--busy' : '')}>
      <a className="pp-image-link" href={item.avatarUrl} target="_blank" rel="noreferrer" title="In voller Größe öffnen">
        <img className="pp-image" src={item.avatarUrl} alt={`Profilbild von ${item.username}`} loading="lazy" />
      </a>
      <div className="pp-meta">
        <div className="pp-username" title={item.userId}>@{item.username}</div>
        <div className="pp-date">{formatDate(item.updatedAt)}</div>
      </div>
      <div className="pp-actions">
        <button
          type="button"
          className={'pp-btn' + (armed ? ' pp-btn--armed' : '')}
          onClick={handleRemoveClick}
          disabled={busy}
        >
          {armed ? 'Wirklich entfernen?' : 'Bild entfernen'}
        </button>
        <button type="button" className="pp-btn pp-btn--danger" onClick={() => onDeleteUser(item)} disabled={busy}>
          Account löschen
        </button>
      </div>
    </div>
  );
}

export default function ProfilePictures() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [busyIds, setBusyIds] = useState(() => new Set());
  const [confirmUser, setConfirmUser] = useState(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);

  const loadPage = useCallback(async (nextPage, { replace = false } = {}) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await ProfilePicturesAPI.list({ page: nextPage, size: PAGE_SIZE });
      const fetched = Array.isArray(data?.items) ? data.items : [];
      setItems((prev) => {
        if (replace) return fetched;
        const known = new Set(prev.map((item) => item.userId));
        return [...prev, ...fetched.filter((item) => !known.has(item.userId))];
      });
      setPage(nextPage);
      setTotalItems(Number(data?.totalItems) || 0);
      setHasMore(nextPage + 1 < (Number(data?.totalPages) || 0));
    } catch (err) {
      console.error('Fehler beim Laden der Profilbilder:', err);
      setError('Konnte Profilbilder nicht laden.');
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, []);

  const reload = useCallback(() => loadPage(0, { replace: true }), [loadPage]);

  useEffect(() => { reload(); }, [reload]);

  // Auto-load the next page when the end of the grid scrolls into view.
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) loadPage(page + 1);
    }, { rootMargin: '600px 0px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, page, loadPage]);

  const markBusy = (userId, busy) => setBusyIds((prev) => {
    const next = new Set(prev);
    if (busy) next.add(userId); else next.delete(userId);
    return next;
  });

  const dropItem = (userId) => {
    setItems((prev) => prev.filter((item) => item.userId !== userId));
    setTotalItems((count) => Math.max(0, count - 1));
  };

  const handleRemovePicture = async (item) => {
    markBusy(item.userId, true);
    try {
      setError(null);
      await ProfilePicturesAPI.removePicture(item.userId);
      dropItem(item.userId);
    } catch (err) {
      console.error('Fehler beim Entfernen des Profilbilds:', err);
      setError(`Konnte das Bild von @${item.username} nicht entfernen.`);
    } finally {
      markBusy(item.userId, false);
    }
  };

  const handleDeleteUser = async () => {
    const item = confirmUser;
    setConfirmUser(null);
    if (!item) return;
    markBusy(item.userId, true);
    try {
      setError(null);
      await ProfilePicturesAPI.deleteUser(item.userId);
      dropItem(item.userId);
    } catch (err) {
      console.error('Fehler beim Löschen des Accounts:', err);
      setError(`Konnte den Account von @${item.username} nicht löschen.`);
    } finally {
      markBusy(item.userId, false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <PageTitle title="Profilbilder" description="Die neuesten Profilbilder der Nutzer. Entferne Bilder, die nicht passen, oder sperre einen Account." />
      </div>

      <div className="pp-toolbar">
        <span className="pp-count">
          {totalItems} {totalItems === 1 ? 'Profilbild' : 'Profilbilder'}
          {items.length < totalItems ? ` · ${items.length} geladen` : ''}
        </span>
        <button className="btn-add btn-add--green" type="button" onClick={reload} disabled={loading}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" strokeLinejoin="round" width="13" height="13" aria-hidden="true">
            <path d="M3 12a9 9 0 1 0 9-9"/><path d="M3 3v6h6"/>
          </svg>
          Neu laden
        </button>
      </div>

      {error && <p className="pp-error">{error}</p>}

      {!loading && items.length === 0 ? (
        <div className="pp-empty">Noch keine hochgeladenen Profilbilder.</div>
      ) : (
        <div className="pp-grid">
          {items.map((item) => (
            <PictureCard
              key={item.userId}
              item={item}
              busy={busyIds.has(item.userId)}
              onRemovePicture={handleRemovePicture}
              onDeleteUser={setConfirmUser}
            />
          ))}
        </div>
      )}

      <div ref={sentinelRef} className="pp-sentinel">
        {loading && <div className="pp-spinner" aria-label="Lädt Profilbilder" />}
        {!loading && hasMore && (
          <button className="pp-btn" type="button" onClick={() => loadPage(page + 1)}>Mehr laden</button>
        )}
      </div>

      <ConfirmDeleteModal
        open={!!confirmUser}
        title={confirmUser ? `Account @${confirmUser.username} löschen?` : ''}
        message="Der Keycloak-Account, das Profil, alle Freundschaften und das Profilbild werden dauerhaft entfernt. Der User kann sich danach nicht mehr einloggen."
        confirmLabel="Account endgültig löschen"
        onConfirm={handleDeleteUser}
        onCancel={() => setConfirmUser(null)}
      />
    </div>
  );
}
